import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { SummaryLayoutItem } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import type { RuntimeFieldSlot, RuntimeLayout } from './runtimeLayoutAdapter'

export type MandatoryFieldError = {
  fieldId: string
  label: string
}

export type MandatoryValidationResult = {
  valid: boolean
  errors: MandatoryFieldError[]
}

const EXCLUDED_TYPES = new Set<string>(['computed', 'interator_simple', 'boolean'])

/** Normalizes API / metadata `mandatory` (boolean, 0/1, string) for validation and UI. */
export function isFieldMandatory(mandatory: unknown): boolean {
  if (mandatory === true || mandatory === 1) return true
  if (typeof mandatory === 'string') {
    const s = mandatory.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

function metadataMap(fields: ObjectInventoryField[]): Map<string, ObjectInventoryField> {
  return new Map(fields.map((f) => [String(f.id), f]))
}

function parseCompositeLeafFieldId(gridCellId: string): string | null {
  const marker = '#f:'
  const idx = gridCellId.lastIndexOf(marker)
  if (idx < 0) return null
  return gridCellId.slice(idx + marker.length).trim() || null
}

function parseParentAccordionGridFieldId(gridId: string): string | null {
  const rootIdx = gridId.indexOf('-root::')
  if (rootIdx < 0) return null
  const prefix = gridId.slice(0, rootIdx)
  const parts = prefix.split('-')
  if (parts.length < 4) return null
  const candidate = parts[parts.length - 1]
  return candidate && candidate !== 'key' ? candidate : null
}

function resolveGridFieldId(
  item: GridFieldType,
  meta: Map<string, ObjectInventoryField>,
): string | null {
  const sid = String(item.id ?? '').trim()
  if (!sid) return null
  if (meta.has(sid)) return sid

  const leafNested = item.nested_binding?.leaf_field_id
  if (leafNested != null) {
    const leaf = String(leafNested)
    if (meta.has(leaf)) return leaf
  }

  const fromComposite = parseCompositeLeafFieldId(sid)
  if (fromComposite && meta.has(fromComposite)) return fromComposite

  const fromParent = parseParentAccordionGridFieldId(sid)
  if (fromParent && meta.has(fromParent)) return fromParent

  const prefixed = sid.match(/^(?:parent|element):([^:]+):.+$/)
  if (prefixed?.[1] && meta.has(prefixed[1])) return prefixed[1]

  return meta.has(sid) ? sid : null
}

function collectMandatoryIdsFromTemplate(
  template: GridFieldType[],
  meta: Map<string, ObjectInventoryField>,
): string[] {
  const seen = new Set<string>()
  const out: string[] = []

  const add = (fieldId: string | null) => {
    if (!fieldId) return
    const def = meta.get(fieldId)
    if (!isFieldMandatory(def?.mandatory) || EXCLUDED_TYPES.has(String(def.type))) return
    if (seen.has(fieldId)) return
    seen.add(fieldId)
    out.push(fieldId)
  }

  for (const item of template) {
    const itemType = item.type ?? 'field'
    if (itemType === 'field') {
      add(resolveGridFieldId(item, meta))
      continue
    }
    if (itemType === 'table' && item.summary_mode && item.summary_layout?.length) {
      for (const sl of item.summary_layout as SummaryLayoutItem[]) {
        if (sl.type && sl.type !== 'field') continue
        add(String(sl.ref_id))
      }
    }
  }
  return out
}

export function collectMandatoryFieldIdsFromLayout(
  layout: RuntimeLayout,
  fieldMetadata: ObjectInventoryField[],
): string[] {
  const meta = metadataMap(fieldMetadata)
  const template: GridFieldType[] = []
  for (const tab of layout.tabs) {
    for (const section of tab.sections) {
      for (const item of section.items) {
        if (item.kind === 'field') {
          template.push(item.layoutItem)
        }
      }
    }
  }
  return collectMandatoryIdsFromTemplate(template, meta)
}

/** Own editable field slots on the main grid (excludes parent/linked read-only cards). */
export function collectMandatorySlotsFromLayout(layout: RuntimeLayout): RuntimeFieldSlot[] {
  const slots: RuntimeFieldSlot[] = []
  for (const tab of layout.tabs) {
    for (const section of tab.sections) {
      for (const item of section.items) {
        if (item.kind !== 'field') continue
        slots.push(item)
      }
    }
  }
  return slots
}

export function isEmptyMandatoryValue(fieldType: string, value: unknown): boolean {
  if (value == null) return true
  if (Array.isArray(value)) return value.length === 0
  // Пустая строка после trim; для number не считаем «визуальный» 0 из coerce — null уже выше.
  if (typeof value === 'number' && Number.isNaN(value)) return true
  const s = String(value).trim()
  if (fieldType === 'list' || fieldType === 'list_types') {
    return s === '' || s === '[]'
  }
  return s === ''
}

export function validateMandatoryFieldValues(
  fieldIds: string[],
  values: Record<string, unknown>,
  fieldMetadata: ObjectInventoryField[],
): MandatoryValidationResult {
  const meta = metadataMap(fieldMetadata)
  const errors: MandatoryFieldError[] = []

  for (const fieldId of fieldIds) {
    const def = meta.get(fieldId)
    if (!isFieldMandatory(def?.mandatory) || EXCLUDED_TYPES.has(String(def.type))) continue
    const raw = values[fieldId]
    if (!isEmptyMandatoryValue(String(def.type), raw)) continue
    errors.push({
      fieldId,
      label: def.name?.trim() || fieldId,
    })
  }

  return { valid: errors.length === 0, errors }
}

export function validateMandatoryFromLayout(
  layout: RuntimeLayout,
  values: Record<string, unknown>,
  fieldMetadata: ObjectInventoryField[],
): MandatoryValidationResult {
  const ids = collectMandatoryFieldIdsFromLayout(layout, fieldMetadata)
  return validateMandatoryFieldValues(ids, values, fieldMetadata)
}

/** Все обязательные поля типа (включая не вынесенные на раскладку). */
export function collectAllMandatoryFieldIds(fieldMetadata: ObjectInventoryField[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const def of fieldMetadata) {
    const fieldId = String(def.id ?? '').trim()
    if (!fieldId) continue
    if (!isFieldMandatory(def.mandatory) || EXCLUDED_TYPES.has(String(def.type))) continue
    if (seen.has(fieldId)) continue
    seen.add(fieldId)
    out.push(fieldId)
  }
  return out
}

export function validateAllMandatoryFieldValues(
  values: Record<string, unknown>,
  fieldMetadata: ObjectInventoryField[],
): MandatoryValidationResult {
  return validateMandatoryFieldValues(
    collectAllMandatoryFieldIds(fieldMetadata),
    values,
    fieldMetadata,
  )
}
